<script setup lang="ts">
import { Button } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type CatalogTask, CRAFTED_NOT_IN_PLAN_MESSAGE, type DefinitionIssue, isCraftedRefusal } from '@/api'

/*
 * One definition issue: its severity and message. An issue about a task this enterprise does not have (a successor
 * or return target it names) also says where to get it: Clone, when the catalog holds a task by that slug, and
 * always a link to the catalog's Tasks list searched for the slug, so nobody has to go looking for it.
 */
const props = defineProps<{ issue: DefinitionIssue }>()
/** A clone landed: the issue it fixed is stale, so the page reads its issues again. */
const emit = defineEmits<{ cloned: [] }>()

const entry = ref<CatalogTask | null>(null)
const state = ref<'idle' | 'cloning' | 'cloned'>('idle')
const error = ref<string | null>(null)

const catalogLink = computed(() =>
    props.issue.missingTask ? { path: '/catalog', query: { kind: 'task', search: props.issue.missingTask } } : null,
)

onMounted(async () => {
    const slug = props.issue.missingTask
    if (!slug) return
    try {
        // Filter on the slug alone (not names) and read the largest page, so the exact slug is found among near ones.
        const listed = await api.catalogTasks({ slug, limit: 200 })
        // Only an exact slug not cloned yet is offered. A deprecated entry cannot be cloned (the API refuses it), as
        // the Catalog's own Clone button knows.
        entry.value =
            listed.data.find(row => row.slug === slug && row.clonedByMe !== true && row.status !== 'deprecated') ?? null
    } catch {
        // No Clone, then: the catalog link still leads there.
    }
})

async function clone(): Promise<void> {
    if (!entry.value || state.value !== 'idle') return
    state.value = 'cloning'
    error.value = null
    try {
        // A clone names no team: a task belongs to a role, and every team where a member holds it runs it.
        await api.cloneCatalogTask(entry.value.id)
        state.value = 'cloned'
        emit('cloned')
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        error.value = isCraftedRefusal(caught) ? CRAFTED_NOT_IN_PLAN_MESSAGE : `Could not clone (${code}).`
        state.value = 'idle'
    }
}
</script>

<template>
	<div class="space-y-1">
		<p>
			<span :class="issue.severity === 'error' ? 'text-destructive' : ''">{{ issue.severity }}:</span>
			{{ issue.message }}
		</p>
		<p v-if="catalogLink" class="flex flex-wrap items-center gap-2" data-testid="missing-task-actions">
			<Button
				v-if="entry"
				type="button"
				size="sm"
				:variant="state === 'cloned' ? 'success' : 'default'"
				:disabled="state !== 'idle'"
				data-testid="missing-task-clone"
				@click="clone"
			>
				{{ state === 'cloning' ? 'Cloning…' : state === 'cloned' ? 'Cloned' : `Clone ${entry.name?.trim() || entry.slug} from the catalog` }}
			</Button>
			<RouterLink class="underline" :to="catalogLink" data-testid="missing-task-catalog">Find it in the catalog</RouterLink>
			<span v-if="error" class="text-destructive">{{ error }}</span>
		</p>
	</div>
</template>
